import api from 'services/api';
import { flattenQuery } from 'helpers/flattenQuery';
import type { DateRange, FilterType, SortOrder } from 'hooks/useListQuery';
import { array, bool, enforce, nullable, num, object, optional, runSchema, str, transform, unknownValue, withDefault, type FieldParser } from 'validation';
import { nodeInstanceRowCoreFields, type NodeInstanceRowCore } from './types';

// Loader for `GET /node_instances` (flat paginated list). Wire shape re-derived
// from nap-backend `getNodeInstances.js` flat-list SELECT (~490-645). See
// `nodeInstanceWire.ts` for the snake_case / mutability / drop-unknown rationale.

/** One row of the flat node-instance list (`NodeInstanceRowCore` + list-only columns). */
export interface NodeInstanceListRow extends NodeInstanceRowCore {
  queue_zone_name: string | null;
  has_child_nodes: boolean;
  has_note: boolean;
  file_event_zone_name: string | null;
  file_event_name: string | null;
  submit_and_forget: boolean;
}

/** Server-driven column descriptor (the `config` array). */
export interface NodeInstanceHeaderConfig {
  header_key: string;
  header_type: string;
  label: string;
}

/** Server-driven filter descriptor (the `filtersConfig` array). Mirrors useListQuery's FilterConfig shape. */
export interface NodeInstanceListFilterConfig {
  key: string;
  label: string;
  type: FilterType;
  options?: string[];
  default?: unknown;
}

/**
 * Pagination envelope. Totals are absent on the list response (they come from
 * the separate count endpoint), so `total_records`/`total_pages` are optional.
 */
export interface NodeInstanceListPagination {
  limit: number;
  current_page: number;
  offset?: number;
  total_records?: number;
  total_pages?: number;
}

export interface NodeInstancesPage {
  nodes: NodeInstanceListRow[];
  pagination: NodeInstanceListPagination;
  config: NodeInstanceHeaderConfig[];
  filtersConfig: NodeInstanceListFilterConfig[];
}

/**
 * Query params for the list. Scalar knobs are typed; date filters arrive as a
 * `DateRange` before `flattenQuery` splits them into `_from`/`_to`, and the
 * remaining server-config-driven filters come through the index signature.
 */
export interface FetchNodeInstancesParams {
  page?: number;
  limit?: number;
  sortColumn: string;
  sortOrder: SortOrder;
  parentOnly?: boolean;
  parentNodeInstanceId?: number | null;
  search?: string;
  queue_id?: number | null;
  queue_name?: string | null;
  worker_id?: number | null;
  worker_name?: string | null;
  status?: string[];
  node_definition_id?: string;
  type?: string[];
  created_at_to?: string;
  created_at_from?: string;
  scheduled_start_datetime_from?: string;
  scheduled_start_datetime_to?: string;
  scheduled_start_datetime_local_from?: string;
  scheduled_start_datetime_local_to?: string;
  node_instance_id?: string | number;
  scheduled_start_timezone?: string;
  name?: string;
  folder?: string;
  zone_name?: string;
  [key: string]: string | number | boolean | string[] | DateRange | null | undefined;
}

const nodeInstanceListRowSchema: FieldParser<NodeInstanceListRow> = object<NodeInstanceListRow>({
  ...nodeInstanceRowCoreFields,
  queue_zone_name: nullable(str()),
  has_child_nodes: bool(),
  has_note: bool(),
  file_event_zone_name: nullable(str()),
  file_event_name: nullable(str()),
  submit_and_forget: bool(),
});

// `nodes` is strict (a missing array is a broken response -> throw). The
// pagination/config/filtersConfig envelope members default when absent so
// callers that hit the same endpoint for a single row (e.g. getNodeInstance,
// which only reads `nodes`) don't need to mock the whole envelope.
const listResponseSchema: FieldParser<NodeInstancesPage> = object<NodeInstancesPage>({
  nodes: array(nodeInstanceListRowSchema),
  pagination: withDefault(
    object<NodeInstanceListPagination>({
      limit: num(),
      current_page: num(1),
      offset: optional(num()),
      total_records: optional(num()),
      total_pages: optional(num()),
    }),
    { limit: 0, current_page: 1 }
  ),
  config: withDefault(
    array(
      object<NodeInstanceHeaderConfig>({
        header_key: str(),
        header_type: str(),
        label: str(),
      })
    ),
    []
  ),
  filtersConfig: withDefault(
    array(
      object<NodeInstanceListFilterConfig>({
        key: str(),
        label: str(),
        // Backend may emit filter types beyond the FE FilterType union (e.g.
        // 'duration'); pass any string through unchanged, as today's `any` did.
        type: transform(str(), (v) => v as FilterType, 'search'),
        options: optional(array(str())),
        default: optional(unknownValue()),
      })
    ),
    []
  ),
});

/**
 * Fetches the flat, paginated node-instance list, validated at the boundary.
 * Rows whose identity (`node_instance_id`/`parent_node_instance_id`) fails to
 * parse are dropped; a structurally malformed envelope throws.
 */
export const getNodeInstances = async (params: FetchNodeInstancesParams, source = 'getNodeInstances'): Promise<NodeInstancesPage> => {
  const res = await api.get('/node_instances', { params: flattenQuery(params) });
  const parsed = runSchema(listResponseSchema, res.data, source);
  if (!parsed.ok) {
    throw new Error(`${source}: response failed validation`);
  }
  return enforce(parsed.value, parsed.warnings, ['nodes[*].node_instance_id', 'nodes[*].parent_node_instance_id']);
};
